import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield } from 'lucide-react';
import Navbar from '../components/Navbar';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PrivacyPolicy = () => {
  const navigate = useNavigate();
  const [dynamicContent, setDynamicContent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/site-content/privacy-policy`)
      .then(res => { if (res.data.content) setDynamicContent(res.data.content); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const Section = ({ number, title, children }) => (
    <div className="mb-10">
      <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-start gap-3">
        <span className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-sm font-bold mt-0.5">
          {number}
        </span>
        {title}
      </h2>
      <div className="pl-11 space-y-3 text-slate-700 leading-relaxed">{children}</div>
    </div>
  );

  return (
    <div className="min-h-screen bg-white pt-16">
      <Navbar />

      <div className="px-6 md:px-12 lg:px-24 pt-6">
        <div className="max-w-3xl mx-auto">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-slate-500 hover:text-slate-900 transition-colors text-sm font-medium"
            data-testid="back-from-privacy"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
        </div>
      </div>

      <article className="px-6 md:px-12 lg:px-24 py-8">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl bg-blue-600 flex items-center justify-center">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold text-slate-900" data-testid="privacy-title">Privacy Policy</h1>
              <p className="text-slate-500 text-sm">Proptick</p>
            </div>
          </div>

          <div className="border-t border-slate-100 pt-8">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : dynamicContent ? (
              <div className="prose-slate">
                {dynamicContent.split('\n').map((line, i) => {
                  const trimmed = line.trim();
                  if (!trimmed) return null;
                  const numbered = trimmed.match(/^(\d+)\.\s+(.+)/);
                  if (numbered) {
                    return (
                      <h2 key={i} className="text-xl font-bold text-slate-900 mb-4 mt-10 flex items-start gap-3">
                        <span className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-sm font-bold mt-0.5">{numbered[1]}</span>
                        {numbered[2]}
                      </h2>
                    );
                  }
                  if (trimmed.startsWith('- ') || trimmed.startsWith('• ')) {
                    return <li key={i} className="text-slate-600 ml-11 list-disc">{trimmed.slice(2)}</li>;
                  }
                  return <p key={i} className="text-slate-700 leading-relaxed mb-3 pl-11">{trimmed}</p>;
                })}
              </div>
            ) : (
              <>
            <p className="text-slate-700 leading-relaxed mb-4">
              Proptick ("Company", "we", "our", or "us"), headquartered in Dubai, United Arab Emirates, is committed to protecting your privacy and ensuring that your personal data is handled in a safe and responsible manner.
            </p>
            <p className="text-slate-700 leading-relaxed mb-4">
              This Privacy Policy explains how we collect, use, disclose, and safeguard personal data when you access or use our website, mobile applications, and related services (collectively, the "Platform").
            </p>
            <p className="text-slate-700 leading-relaxed mb-8">
              By accessing or using the Platform, or by providing your personal data to us, you consent to the practices described in this Policy. Where required, we may seek your explicit consent for specific processing activities.
            </p>

            <Section number="1" title="Scope of Policy">
              <p>This Policy applies to all users of the Platform, including but not limited to:</p>
              <ul className="list-disc list-inside space-y-1 text-slate-600">
                <li>Buyers and renters</li>
                <li>Property owners and landlords</li>
                <li>Real estate agents, brokers, and developers</li>
                <li>Visitors and general users</li>
              </ul>
              <p>"Personal Data" refers to any information that can identify an individual, directly or indirectly.</p>
            </Section>

            <Section number="2" title="Personal Data We Collect">
              <p className="font-semibold text-slate-900">A. Information You Provide Directly</p>
              <p>We collect personal data that you voluntarily provide, including:</p>
              <ul className="list-disc list-inside space-y-1 text-slate-600">
                <li><strong>Personal Information:</strong> Name, email address, phone number, address, and account login credentials</li>
                <li><strong>Property Information:</strong> Property type, location, specifications, pricing, images, and amenities</li>
                <li><strong>Verification Documents:</strong> Identification (passport, Emirates ID, driver's license) and property ownership documentation</li>
                <li><strong>Communication Records:</strong> Emails, messages, calls, feedback, and support queries</li>
                <li><strong>Voice Data:</strong> Voice recordings used in property listings (if applicable)</li>
                <li><strong>Other Information:</strong> Any additional details you choose to share</li>
              </ul>

              <p className="font-semibold text-slate-900 mt-6">B. Information Collected Automatically</p>
              <p>When you use our Platform, we may collect:</p>
              <ul className="list-disc list-inside space-y-1 text-slate-600">
                <li><strong>Usage Data:</strong> Search activity, preferences, time spent, interactions</li>
                <li><strong>Technical Data:</strong> Device type, IP address, browser, operating system</li>
                <li><strong>Location Data:</strong> Approximate location derived from IP or device settings</li>
                <li><strong>Communication Data:</strong> Interactions with other users on the Platform</li>
                <li><strong>Cookies & Tracking Technologies:</strong> To enhance user experience and platform performance</li>
                <li><strong>Transaction Data:</strong> Details of services purchased (excluding financial credentials)</li>
                <li><strong>App Permissions:</strong> Camera, storage, notifications, and location (subject to your approval)</li>
              </ul>

              <p className="font-semibold text-slate-900 mt-6">C. Information from Third Parties</p>
              <p>We may receive information from:</p>
              <ul className="list-disc list-inside space-y-1 text-slate-600">
                <li>Marketing and advertising partners</li>
                <li>Publicly available sources</li>
                <li>Third-party login providers (e.g., SSO, MFA)</li>
              </ul>
            </Section>

            <Section number="3" title="How We Use Your Personal Data">
              <p className="font-semibold text-slate-900">A. Service Delivery</p>
              <ul className="list-disc list-inside space-y-1 text-slate-600 mb-4">
                <li>Account creation and management</li>
                <li>Property listing and promotion</li>
                <li>Connecting users (buyers, sellers, agents, financial institutions)</li>
                <li>Providing personalized recommendations and search results</li>
              </ul>

              <p className="font-semibold text-slate-900">B. Marketing and Promotions</p>
              <ul className="list-disc list-inside space-y-1 text-slate-600 mb-4">
                <li>Sending updates, offers, and promotional communications (with consent)</li>
                <li>Sharing relevant opportunities based on your interests</li>
              </ul>

              <p className="font-semibold text-slate-900">C. Third-Party Engagement</p>
              <p className="mb-4">Sharing your details with banks, financial institutions, or partners if you express interest in their services.</p>

              <p className="font-semibold text-slate-900">D. Platform Improvement</p>
              <ul className="list-disc list-inside space-y-1 text-slate-600 mb-4">
                <li>Enhancing user experience and platform functionality</li>
                <li>Conducting research, analytics, and performance tracking</li>
              </ul>

              <p className="font-semibold text-slate-900">E. Fraud Prevention and Security</p>
              <ul className="list-disc list-inside space-y-1 text-slate-600 mb-4">
                <li>Verifying user identity and listings</li>
                <li>Detecting and preventing fraud or misuse</li>
              </ul>

              <p className="font-semibold text-slate-900">F. Technical Support</p>
              <ul className="list-disc list-inside space-y-1 text-slate-600 mb-4">
                <li>Troubleshooting issues</li>
                <li>Maintaining backups and system stability</li>
              </ul>

              <p className="font-semibold text-slate-900">G. Legal and Compliance</p>
              <ul className="list-disc list-inside space-y-1 text-slate-600 mb-4">
                <li>Meeting legal obligations</li>
                <li>Enforcing terms and protecting rights</li>
              </ul>

              <p className="font-semibold text-slate-900">H. Communication</p>
              <ul className="list-disc list-inside space-y-1 text-slate-600">
                <li>Responding to queries, feedback, and complaints</li>
                <li>Providing service-related notifications</li>
              </ul>
            </Section>

            <Section number="4" title="Cookies and Tracking Technologies">
              <p>We use cookies and similar technologies to:</p>
              <ul className="list-disc list-inside space-y-1 text-slate-600">
                <li>Recognize users and improve experience</li>
                <li>Analyze platform performance</li>
                <li>Store preferences</li>
              </ul>
              <p>You may control cookie settings through your browser; however, disabling cookies may impact functionality.</p>
            </Section>

            <Section number="5" title="Sharing of Personal Data">
              <p>We may share your personal data with:</p>

              <p className="font-semibold text-slate-900 mt-4">A. Service Providers</p>
              <p className="mb-3">Vendors, consultants, and partners who support platform operations, verification, and service delivery.</p>

              <p className="font-semibold text-slate-900">B. Other Users</p>
              <p className="mb-3">To enable communication between buyers, sellers, and agents.</p>

              <p className="font-semibold text-slate-900">C. Financial Institutions</p>
              <p className="mb-3">Banks or financial partners when you request related services (e.g., home loans).</p>

              <p className="font-semibold text-slate-900">D. Legal Authorities</p>
              <p className="mb-3">Where required by law or to protect legal rights.</p>

              <p className="font-semibold text-slate-900">E. Corporate Transactions</p>
              <p className="mb-3">In case of mergers, acquisitions, or restructuring.</p>

              <p className="font-semibold text-slate-900">F. Professional Advisors</p>
              <p>Auditors, legal advisors, and consultants on a need-to-know basis.</p>
            </Section>

            <Section number="6" title="Your Rights">
              <p>You may:</p>
              <ul className="list-disc list-inside space-y-1 text-slate-600">
                <li>Access, update, or correct your personal data</li>
                <li>Request deletion of your data</li>
                <li>Withdraw consent for data processing</li>
                <li>Opt out of marketing communications</li>
              </ul>
              <p className="mt-3">
                Requests can be made by contacting us at: <a href="mailto:privacy@proptick.com" className="text-blue-600 font-medium hover:underline">privacy@proptick.com</a>
              </p>
              <p className="text-sm text-slate-500 mt-2">Note: Some data may be required for core services, and withdrawal may limit access to the Platform.</p>
            </Section>

            <Section number="7" title="Data Storage and Security">
              <ul className="list-disc list-inside space-y-1 text-slate-600">
                <li>Your data is stored securely and processed primarily in the United Arab Emirates, with possible transfers to other jurisdictions</li>
                <li>We implement administrative, technical, and physical safeguards to protect data</li>
                <li>Third-party processors are required to meet equivalent security standards</li>
              </ul>
            </Section>

            <Section number="8" title="Data Retention">
              <p>We retain personal data:</p>
              <ul className="list-disc list-inside space-y-1 text-slate-600">
                <li>As long as necessary to provide services</li>
                <li>To comply with legal obligations</li>
                <li>To resolve disputes and enforce agreements</li>
              </ul>
              <p className="mt-3">We may retain anonymized data indefinitely for analytics and improvement purposes.</p>
            </Section>

            <Section number="9" title="Third-Party Links and Services">
              <p>Our Platform may contain links to third-party websites or services. We are not responsible for their privacy practices. Users are encouraged to review their respective privacy policies.</p>
            </Section>

            <Section number="10" title="Children's Privacy">
              <p>The Platform is not intended for individuals under the age of 18. We do not knowingly collect data from minors.</p>
            </Section>

            <Section number="11" title="Updates to This Policy">
              <p>We may update this Privacy Policy periodically. Changes will be effective upon publication on the Platform.</p>
            </Section>

            <Section number="12" title="Contact and Grievance Redressal">
              <p>For questions, concerns, or complaints regarding this Policy or your personal data, please contact:</p>
              <div className="bg-slate-50 rounded-xl p-6 mt-3 border border-slate-100">
                <p className="font-semibold text-slate-900 text-lg">Proptick</p>
                <p className="text-slate-600">Dubai, United Arab Emirates</p>
                <p className="text-slate-600 mt-1">
                  Email: <a href="mailto:privacy@proptick.com" className="text-blue-600 font-medium hover:underline">privacy@proptick.com</a>
                </p>
              </div>
            </Section>
              </>
            )}
          </div>
        </div>
      </article>
    </div>
  );
};

export default PrivacyPolicy;
